/**
 * Headless network/device diagnosis for the virtual event login gate.
 *
 * Runs a focused subset of the full /event-diagnostic page probes — only the
 * checks that are load-bearing for Agora 1:1 booth video calls — and returns a
 * structured report. The caller (Login.vue) shows a soft-warning snackbar if
 * `criticalIssues.length > 0` but never blocks the user from entering the event.
 */

export interface ServiceProbeResult {
	group: string;
	label: string;
	url: string;
	state: "ok" | "opaque" | "fail" | "pending";
	ms: number | null;
	error?: string;
}

export interface WebRTCProbeResult {
	supported: boolean;
	candidateTypes?: { host: boolean; srflx: boolean; relay: boolean };
	srflxAddresses?: string[];
	error?: string;
}

export interface DiagnosisReport {
	generatedAt: string;
	url: string;
	userAgent: string;
	platform: string;
	online: boolean;
	timeZone: string;
	connection: {
		effectiveType?: string;
		downlink?: number;
		rtt?: number;
		type?: string;
	} | null;
	services: ServiceProbeResult[];
	webrtc: WebRTCProbeResult;
	criticalIssues: string[];
}

const AGORA_CRITICAL_SERVICES: Array<{ group: string; label: string; url: string }> = [
	{ group: "Agora", label: "Agora SD-RTN", url: "https://web-2.ap.sd-rtn.com/" },
	{ group: "Firebase", label: "Firebase Auth domain", url: "https://e2monair.firebaseapp.com/" },
	{ group: "Firebase", label: "Google APIs", url: "https://www.googleapis.com/" },
	{ group: "Firebase", label: "Cloud Functions", url: "https://us-central1-e2monair.cloudfunctions.net/" },
	{ group: "Platform", label: "Branding/Resources CDN", url: "https://resources.e2m.live/favicon.ico" },
];

const STUN_SERVERS = [
	"stun:stun.l.google.com:19302",
	"stun:stun.agora.io:3478",
];

function fetchProbe(url: string, ms: number): Promise<{ ok: boolean; ms: number; status?: number; type?: string; error?: string }> {
	const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
	const t = setTimeout(() => { if (ctrl) ctrl.abort(); }, ms);
	const t0 = performance.now();
	return fetch(url, {
		method: "GET",
		mode: "no-cors",
		cache: "no-store",
		...(ctrl ? { signal: ctrl.signal } : {}),
	}).then((res) => {
		clearTimeout(t);
		return { ok: true, ms: Math.round(performance.now() - t0), status: res.status, type: res.type };
	}).catch((err: Error) => {
		clearTimeout(t);
		return { ok: false, ms: Math.round(performance.now() - t0), error: err.message || String(err) };
	});
}

async function probeServices(): Promise<ServiceProbeResult[]> {
	const results: ServiceProbeResult[] = [];
	await Promise.all(
		AGORA_CRITICAL_SERVICES.map(async (s) => {
			const r = await fetchProbe(s.url, 6000);
			const entry: ServiceProbeResult = {
				group: s.group,
				label: s.label,
				url: s.url,
				state: r.ok ? (r.type === "opaque" ? "opaque" : "ok") : "fail",
				ms: r.ms,
			};
			if (!r.ok) entry.error = r.error;
			results.push(entry);
		})
	);
	return results;
}

async function probeRTDB(): Promise<ServiceProbeResult> {
	const url = "wss://e2monair.firebaseio.com/.ws?v=5&ns=e2monair";
	const t0 = performance.now();
	return new Promise<ServiceProbeResult>((resolve) => {
		let ws: WebSocket | null = null;
		let settled = false;
		const timer = setTimeout(() => {
			if (settled) return;
			settled = true;
			try { ws && ws.close(); } catch (_) { /* ignore */ }
			resolve({
				group: "Firebase",
				label: "Firebase Realtime DB (WSS)",
				url,
				state: "fail",
				ms: Math.round(performance.now() - t0),
				error: "WebSocket timeout",
			});
		}, 7000);
		try {
			ws = new WebSocket(url);
			ws.onopen = () => {
				if (settled) return;
				settled = true;
				clearTimeout(timer);
				try { ws && ws.close(); } catch (_) { /* ignore */ }
				resolve({
					group: "Firebase",
					label: "Firebase Realtime DB (WSS)",
					url,
					state: "ok",
					ms: Math.round(performance.now() - t0),
				});
			};
			ws.onerror = () => {
				if (settled) return;
				settled = true;
				clearTimeout(timer);
				resolve({
					group: "Firebase",
					label: "Firebase Realtime DB (WSS)",
					url,
					state: "fail",
					ms: Math.round(performance.now() - t0),
					error: "WebSocket error",
				});
			};
		} catch (e) {
			settled = true;
			clearTimeout(timer);
			resolve({
				group: "Firebase",
				label: "Firebase Realtime DB (WSS)",
				url,
				state: "fail",
				ms: Math.round(performance.now() - t0),
				error: e instanceof Error ? e.message : String(e),
			});
		}
	});
}

async function probeWebRTC(): Promise<WebRTCProbeResult> {
	if (typeof window === "undefined" || !window.RTCPeerConnection) {
		return { supported: false };
	}
	const pc = new RTCPeerConnection({
		iceServers: STUN_SERVERS.map((u) => ({ urls: u })),
	});
	const candidates: RTCIceCandidate[] = [];
	pc.onicecandidate = (e) => { if (e.candidate) candidates.push(e.candidate); };
	pc.createDataChannel("probe");
	try {
		const offer = await pc.createOffer();
		await pc.setLocalDescription(offer);
	} catch (e) {
		try { pc.close(); } catch (_) { /* ignore */ }
		return { supported: true, error: e instanceof Error ? e.message : String(e) };
	}
	await new Promise<void>((resolve) => {
		let done = false;
		pc.onicegatheringstatechange = () => {
			if (pc.iceGatheringState === "complete" && !done) { done = true; resolve(); }
		};
		setTimeout(() => { if (!done) { done = true; resolve(); } }, 6000);
	});

	const hasHost = candidates.some((c) => c.type === "host");
	const hasSrflx = candidates.some((c) => c.type === "srflx");
	const hasRelay = candidates.some((c) => c.type === "relay");
	const srflxAddresses = candidates
		.filter((c) => c.type === "srflx")
		.map((c: RTCIceCandidate & { ip?: string }) => (c.address || c.ip || ""));

	try { pc.close(); } catch (_) { /* ignore */ }

	return {
		supported: true,
		candidateTypes: { host: hasHost, srflx: hasSrflx, relay: hasRelay },
		srflxAddresses,
	};
}

function browserInfo() {
	type NetInfoLike = { effectiveType?: string; downlink?: number; rtt?: number; type?: string };
	const nav: Navigator & {
		connection?: NetInfoLike;
		mozConnection?: NetInfoLike;
		webkitConnection?: NetInfoLike;
	} = navigator;
	const c = nav.connection || nav.mozConnection || nav.webkitConnection;
	return {
		userAgent: navigator.userAgent,
		platform: navigator.platform,
		online: navigator.onLine,
		timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		connection: c ? {
			effectiveType: c.effectiveType,
			downlink: c.downlink,
			rtt: c.rtt,
			type: c.type,
		} : null,
	};
}

/**
 * Run the headless event-diagnosis. Resolves with a report regardless of
 * success/failure of individual probes — callers should inspect `criticalIssues`
 * to decide whether to surface a warning.
 */
export async function runEventDiagnosis(): Promise<DiagnosisReport> {
	const services = await probeServices();
	const rtdb = await probeRTDB();
	services.push(rtdb);
	const webrtc = await probeWebRTC();

	const criticalIssues: string[] = [];

	const agoraSvc = services.find((s) => s.group === "Agora");
	if (agoraSvc && agoraSvc.state === "fail") {
		criticalIssues.push("Agora SD-RTN servers are unreachable — 1:1 booth video calls will not connect.");
	}

	const fbAuth = services.find((s) => s.label.indexOf("Firebase Auth") >= 0);
	if (fbAuth && fbAuth.state === "fail") {
		criticalIssues.push("Firebase Auth domain is blocked — sign-in may fail.");
	}

	const resCdn = services.find((s) => s.label.indexOf("Branding/Resources CDN") >= 0);
	if (resCdn && resCdn.state === "fail") {
		criticalIssues.push("Branding/Resources CDN (resources.e2m.live) is unreachable — event branding, custom fonts, sponsor logos and banner images will fail to load.");
	}

	if (rtdb.state === "fail") {
		criticalIssues.push("Firebase Realtime Database WebSocket is blocked — live updates (chat, presence, booth status) will not work.");
	}

	if (!webrtc.supported) {
		criticalIssues.push("Your browser does not support WebRTC — please switch to a recent Chrome, Edge, Firefox, or Safari.");
	} else if (webrtc.candidateTypes && !webrtc.candidateTypes.srflx) {
		criticalIssues.push("UDP/STUN appears blocked on your network — Agora 1:1 booth video will fail to connect.");
	}

	if (!navigator.onLine) {
		criticalIssues.push("Your device reports it is offline.");
	}

	const info = browserInfo();
	return {
		generatedAt: new Date().toISOString(),
		url: location.href,
		userAgent: info.userAgent,
		platform: info.platform,
		online: info.online,
		timeZone: info.timeZone,
		connection: info.connection,
		services,
		webrtc,
		criticalIssues,
	};
}

/**
 * Build a plain-text report suitable for the body of a mailto: link.
 * Kept short enough that Outlook/Gmail won't reject the URL — heavy detail
 * (full WebRTC candidate dump, all service URLs) is preserved as JSON.
 */
export function buildReportText(report: DiagnosisReport): string {
	const lines: string[] = [];
	lines.push("e2m.live — Event Platform Diagnostic Report");
	lines.push("Generated: " + report.generatedAt);
	lines.push("Page: " + report.url);
	lines.push("");
	lines.push("--- Device / Browser ---");
	lines.push("User agent: " + report.userAgent);
	lines.push("Platform: " + report.platform);
	lines.push("Online: " + report.online);
	lines.push("Time zone: " + report.timeZone);
	if (report.connection) {
		lines.push("Connection: " + JSON.stringify(report.connection));
	}
	lines.push("");
	lines.push("--- Critical issues ---");
	if (report.criticalIssues.length === 0) {
		lines.push("(none detected)");
	} else {
		report.criticalIssues.forEach((c) => lines.push("- " + c));
	}
	lines.push("");
	lines.push("--- Service reachability ---");
	report.services.forEach((s) => {
		lines.push(
			"[" + s.state.toUpperCase() + "] " + s.label + " — " + s.url +
			(s.ms != null ? " (" + s.ms + "ms)" : "") +
			(s.error ? " — " + s.error : "")
		);
	});
	lines.push("");
	lines.push("--- WebRTC ---");
	lines.push(JSON.stringify(report.webrtc, null, 2));
	return lines.join("\n");
}
